import {
  Component,
  OnInit,
  ViewChild,
  Input,
  SimpleChanges,
  Output,
  OnChanges,
  EventEmitter,
} from '@angular/core';
import {
  AuthService,
  DataService,
  ToasterService,
} from '../../../../shared/services';
import { Router } from '@angular/router';
import { GridOptions } from 'ag-grid-community';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { ActionsModel } from '../../../../shared/models';
import { ConfirmModalComponent } from '../../../../shared/modals';
import { GridComponent } from '../../../../shared';
import { ActionsEditComponent } from './edit/edit.component';

import * as _ from 'lodash';

import { BreadCrumbService } from '../../../../shared/services/breadcrumb.service';
import { syncItems } from 'src/utils';

@Component({
  selector: 'app-actions',
  templateUrl: './actions.component.html',
})
export class ActionsListComponent implements OnInit, OnChanges {
  @ViewChild(GridComponent, { static: true }) actionsGrid?: GridComponent;

  @Input() selectedLifecycleModelNodes: any[] = [];
  @Input() sizeColumnsToFit = true;
  @Output() resetModelsGrid = new EventEmitter<any>();
  @Output() changeSelectLifecycleModelNodes = new EventEmitter<any>();
  @Output() changeChildGrid = new EventEmitter<any>();
  // Available items
  items: any[] = [];
  selectedNodes: any[] = [];
  parentFiltered?: any;
  // UI flag
  isLoading = false;
  // Local grid options
  gridOptions?: GridOptions;
  // Modal ref pointer
  bsModalRef?: BsModalRef;
  isReferenceLoaded = false;
  actionsGridAutoGroupColumnDef?: any;
  modelData: any = {};
  typesReference?: any;
  budgetsReference?: any;

  constructor(
    private translate: TranslateService,
    private data: DataService,
    private bsModalService: BsModalService,
    private toastr: ToasterService,
    public auth: AuthService,
    public router: Router,
    public breadCrumbService: BreadCrumbService
  ) {}

  /**
   */
  async ngOnInit(): Promise<void> {
    const tmpGridOptions: GridOptions = {
      getRowId: (row: any) => row.data.id,
      suppressRowClickSelection: false,
      groupSelectsChildren: true,
      rowSelection: 'multiple',
      rowHeight: 30,
      headerHeight: 30,
      animateRows: true,
      floatingFiltersHeight: 30,
      enableCellTextSelection: false,
      suppressContextMenu: true,
      groupDefaultExpanded: -1, // expand all groups by default
      defaultColDef: {
        filter: true,
        sortable: true,
        resizable: true,
        floatingFilter: true,
      },
      columnDefs: [
        {
          headerName: this.translate.instant(
            'headers.fastlc.lifecycle_model.model'
          ),
          field: 'model_name',
          valueGetter: (params: any) => {
            if (params.data) {
              return params.data.model_id;
            }
          },
          filterValueGetter: (params: any) => {
            if (params.data) {
              return params.data.model_name;
            }
          },
          cellRenderer: (params: any) => {
            return this.modelData?.[params.value];
          },
          rowGroup: true,
          hide: true,
        },
        {
          headerName: this.translate.instant(
            'headers.fastlc.model_actions.cost'
          ),
          field: 'cost',
        },
        {
          headerName: this.translate.instant(
            'headers.fastlc.model_actions.type'
          ),
          field: 'type_name',
        },
        {
          headerName: this.translate.instant(
            'headers.fastlc.model_actions.budget'
          ),
          field: 'budget_name',
        },
        {
          headerName: this.translate.instant(
            'headers.fastlc.model_actions.comment'
          ),
          field: 'comment',
        },
        {
          headerName: this.translate.instant(
            'headers.fastlc.lifecycle_model.id'
          ),
          field: 'model_id',
          hide: true,
        },
      ],
    };
    this.actionsGridAutoGroupColumnDef = {
      headerName: this.translate.instant(
        'headers.fastlc.model_actions.description'
      ),
      field: 'description',
      filter: 'agTextColumnFilter',
      filterParams: { newRowsAction: 'keep' },
      cellRendererParams: {
        checkbox: true,
      },
      sortable: true,
      sort: 'asc',
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true,
    };

    this.gridOptions = tmpGridOptions;
    await this.loadItems();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (this.selectedLifecycleModelNodes) {
      this.filterList(this.selectedLifecycleModelNodes);
    }
  }

  clearFilterAndResetModelsGrid(): void {
    this.actionsGrid?.clearAllFilters();
    this.resetModelsGrid.emit();
  }

  resetModelActionGrid(): void {
    this.actionsGrid?.uncheckAll();
  }

  /**
   */
  async loadItems(silent = false): Promise<void> {
    if (!silent) {
      this.isLoading = true;
    }

    this.items = await this.data
      .by('api/fastlc/model-actions', {
        criteria: {
          site: this.auth.getSiteId(),
        },
      })
      .toPromise();
    if (this.sizeColumnsToFit) {
      this.actionsGrid?.gridApi?.sizeColumnsToFit();
      this.sizeColumnsToFit = false;
    }

    this.actionsGrid?.uncheckAll();
    this.filterList(this.selectedLifecycleModelNodes);

    // set data array to use ID as value in valueGetter and Description as display value.
    // This is to allow grouping by ID instead of display value ("name" for eg.) thereby avoid grouping on duplicate display values
    this.items.map((val) => {
      this.modelData[val.model_id] = val.model_name;
    });

    if (!silent) {
      this.isLoading = false;
    }
  }

  changeGrid(): void {
    this.changeChildGrid.emit();
  }

  onSelectionChanged(event: any): void {
    this.selectedNodes = event;
  }

  onRowClicked(event: any): void {
    if (!event) {
      return;
    }
    this.showEditModal([event]);
  }

  filterList(actionMOdels: any): void {
    const instance = this.actionsGrid?.gridApi?.getFilterInstance('model_id');
    if (actionMOdels.length > 0) {
      setTimeout(() => {
        instance?.setModel({
          values: _.map(actionMOdels, (el) => el.id + ''),
        });
        this.actionsGrid?.gridApi?.onFilterChanged();
      });
      return;
    }
    instance?.setModel(null);
    this.actionsGrid?.gridApi?.onFilterChanged();
  }

  /**
   * @param items
   */
  async showEditModal(items?: ActionsModel[]): Promise<void> {
    await this.loadTypesAndBudgets();

    this.bsModalRef = this.bsModalService.show(ActionsEditComponent, {
      initialState: {
        items,
        selectedLifecycleModelNodes: this.selectedLifecycleModelNodes,
        typesReference: this.typesReference,
        budgetsReference: this.budgetsReference,
      },
      class: 'modal-md',
    });
    this.bsModalRef.content.confirmFn = (res: ActionsModel) => {
      this.items = syncItems(res, this.items);
      this.filterList(this.selectedLifecycleModelNodes);

      this.items.map((val) => {
        this.modelData[val.model_id] = val.model_name;
      });

      this.toastr.showSuccess();
    };
  }

  // Load Types and Budgets used in modal edit form
  async loadTypesAndBudgets(): Promise<void> {
    const orderByDesc = { orderBy: { description: 'asc' } };

    const actionTypesPromise = this.data
      .by('api/fastlc/action-types', {
        criteria: {
          account: this.auth.getAccountId(),
        },
        ...orderByDesc,
      })
      .toPromise();

    const actionBudgetsPromise = this.data
      .by('api/fastlc/action-budgets', {
        criteria: {
          account: this.auth.getAccountId(),
        },
        ...orderByDesc,
      })
      .toPromise();

    try {
      const [types, budgets] = await Promise.all([
        actionTypesPromise,
        actionBudgetsPromise,
      ]);

      this.typesReference = _.map(types, (el) => {
        return {
          name: el.description,
          value: el.id,
        };
      });

      this.budgetsReference = _.map(budgets, (el) => {
        return {
          name: el.description,
          value: el.id,
        };
      });
    } catch (error) {
      // Handle error if any of the promises reject
      console.error('Error fetching data:', error);
    }

    // Mark ref as loaded (prevent reload)
    this.isReferenceLoaded = true;
  }

  showMassEditModal(): void {
    if (this.selectedNodes && this.selectedNodes.length > 0) {
      const items = _.map(
        this.selectedNodes,
        (el) => new ActionsModel(el.data)
      );
      this.showEditModal(items);
    }
  }

  /**
   */
  showMassDeleteConfirm(): void {
    this.bsModalRef = this.bsModalService.show(ConfirmModalComponent, {
      initialState: {
        title: 'navigations.delete',
        content: '',
      },
    });
    this.bsModalRef.content.confirmFn = () => {
      const ids = _.map(this.selectedNodes, (el) => el.id);
      this.isLoading = true;
      this.data
        .post('api/fastlc/model-actions/delete-all-singly', ids)
        .subscribe(
          (res) => {
            this.items = this.items.filter(
              (item: any) => !ids.includes(`${item.id}`)
            );
            this.isLoading = false;
            this.toastr.showSuccess('messages.delete');
          },
          (err) => {
            this.isLoading = false;
            this.toastr.showError();
          }
        );
      this.bsModalRef?.hide();
    };
    this.bsModalRef.content.cancelFn = () => {
      this.bsModalRef?.hide();
    };
  }

  myCellCallback = (params: any) => {
    return params.value;
  };

  exportProcessCallBack = (cellParams: any) => {
    if (!cellParams.value) {
      return '';
    }

    // for group row
    if (cellParams.node.field === 'model_name') {
      return this.modelData[cellParams.node.key];
    }

    // for normal row
    if (cellParams.column.colId === 'model_name') {
      return cellParams.node.data.model_name;
    }

    return cellParams.value;
  };
}
